import { useCallback, useMemo, useState } from 'react';
import { OrgChartEmployee } from './types';

export type OrgChartFilters = {
  departments: string[];
  locations: string[];
  teams: string[];
  position: string;
};

const EMPTY_FILTERS: OrgChartFilters = {
  departments: [],
  locations: [],
  teams: [],
  position: '',
};

const hasActiveFilters = (filters: OrgChartFilters): boolean =>
  filters.departments.length > 0 ||
  filters.locations.length > 0 ||
  filters.teams.length > 0 ||
  filters.position.trim() !== '';

const matchesFilters = (
  employee: OrgChartEmployee,
  filters: OrgChartFilters,
): boolean => {
  if (
    filters.departments.length > 0 &&
    (!employee.departmentRef ||
      !filters.departments.includes(employee.departmentRef.id))
  ) {
    return false;
  }

  if (
    filters.locations.length > 0 &&
    (!employee.locationRef ||
      !filters.locations.includes(employee.locationRef.id))
  ) {
    return false;
  }

  if (filters.teams.length > 0) {
    const employeeTeamIds = employee.teams.map((t) => t.id);
    if (!filters.teams.some((id) => employeeTeamIds.includes(id))) {
      return false;
    }
  }

  if (filters.position.trim() !== '') {
    const search = filters.position.toLowerCase().trim();
    if (!employee.position.toLowerCase().includes(search)) {
      return false;
    }
  }

  return true;
};

const addAncestors = (
  matchedIds: Set<string>,
  employeeMap: Map<string, OrgChartEmployee>,
): Set<string> => {
  const result = new Set(matchedIds);
  const queue = Array.from(matchedIds);

  while (queue.length > 0) {
    const id = queue.pop()!;
    const emp = employeeMap.get(id);
    if (emp?.manager?.id && !result.has(emp.manager.id)) {
      result.add(emp.manager.id);
      queue.push(emp.manager.id);
    }
  }

  return result;
};

// "Find" narrows the chart to the searched employee's reporting line — the
// employee plus their manager chain up to the root. Showing the employee alone
// stripped the hierarchy that makes the chart readable at all.
export const getFindModeEmployees = (
  employees: OrgChartEmployee[],
  searchedId: string,
): OrgChartEmployee[] => {
  const employeeMap = new Map(employees.map((e) => [e.id, e]));
  if (!employeeMap.has(searchedId)) return [];

  const idsToShow = addAncestors(new Set([searchedId]), employeeMap);

  // Keep each employee's real subordinate count — the badge reports how many
  // reports exist, not how many are currently drawn, so expanding a node in the
  // chain reveals the siblings the count already promised.
  //
  // Only the searched employee is collapsed. Every ancestor has its chain child
  // on screen, so marking them collapsed would leave the card claiming a state
  // it isn't in and send the first chevron click down the collapse path —
  // pruning the subtree that contains the searched employee.
  return employees
    .filter((e) => idsToShow.has(e.id))
    .map((e) => ({ ...e, isCollapsed: e.id === searchedId }));
};

export const useOrgChartFilters = (employees: OrgChartEmployee[]) => {
  const [filters, setFilters] = useState<OrgChartFilters>(EMPTY_FILTERS);

  const isActive = useMemo(() => hasActiveFilters(filters), [filters]);

  const filteredEmployees = useMemo(() => {
    if (!isActive) return employees;

    const employeeMap = new Map(employees.map((e) => [e.id, e]));

    const matchedIds = new Set<string>();
    for (const emp of employees) {
      if (matchesFilters(emp, filters)) {
        matchedIds.add(emp.id);
      }
    }

    const idsToShow = addAncestors(matchedIds, employeeMap);

    const subordinateCountByManager = new Map<string, number>();
    for (const emp of employees) {
      const managerId = emp.manager?.id;
      if (!managerId) continue;
      if (!idsToShow.has(emp.id)) continue;
      subordinateCountByManager.set(
        managerId,
        (subordinateCountByManager.get(managerId) ?? 0) + 1,
      );
    }

    return employees
      .filter((e) => idsToShow.has(e.id))
      .map((e) => ({
        ...e,
        noOfSubEmployees: subordinateCountByManager.get(e.id) ?? 0,
      }));
  }, [employees, filters, isActive]);

  const clearFilters = useCallback(() => setFilters(EMPTY_FILTERS), []);

  return {
    filters,
    setFilters,
    filteredEmployees,
    hasActiveFilters: isActive,
    clearFilters,
  };
};

export { matchesFilters, addAncestors, hasActiveFilters };
